import type { ExperienceTierData } from '../types';

/**
 * Replicates the VLOOKUP reference table at B99:L108 in the Estimator Excel file.
 *
 * Level 1 = most experienced, Level 10 = least.
 * Level 0 = ineligible (combined score below the level-10 minimum of 2).
 *   The Excel returns #N/A from VLOOKUP for score < 2, which propagates as 0
 *   for the LTV haircut and fee rate, giving C3=$0 and effectiveLtc=90%.
 *
 * Excel columns mapped:
 *   B = level
 *   C = minCombinedScore  (Z28 threshold: built score + good repayments)
 *   D = minRepayments     (AF28 threshold: BFI repayments independently)
 *   E = ltvHaircut        (base LTV ceiling, G3)
 *   H = feeRate           (1−feeRate = base LTC, G4)
 *   L = subLandAdvanceFactor (AK40, used in C5 when land not owned)
 */

/** Sentinel tier used when the borrower's combined score is below the level-10 minimum. */
export const INELIGIBLE_EXPERIENCE_TIER: ExperienceTierData = {
  level: 0,
  minCombinedScore: 0,
  minRepayments: 0,
  ltvHaircut: 0,        // C3 = 0 × AV = $0
  feeRate: 0,           // effectiveLtc = MIN(90%, 1.0) = 90%
  subLandAdvanceFactor: 0, // C5 = 0 × constructionCost = $0 when land not owned
};

export const EXPERIENCE_TIER_TABLE: readonly ExperienceTierData[] = [
  { level: 1,  minCombinedScore: 100, minRepayments: 25, ltvHaircut: 0.75, feeRate: 0.10, subLandAdvanceFactor: 1.00 },
  { level: 2,  minCombinedScore: 50,  minRepayments: 15, ltvHaircut: 0.72, feeRate: 0.10, subLandAdvanceFactor: 1.00 },
  { level: 3,  minCombinedScore: 40,  minRepayments: 10, ltvHaircut: 0.70, feeRate: 0.12, subLandAdvanceFactor: 1.00 },
  { level: 4,  minCombinedScore: 20,  minRepayments: 5,  ltvHaircut: 0.70, feeRate: 0.14, subLandAdvanceFactor: 1.00 },
  { level: 5,  minCombinedScore: 20,  minRepayments: 2,  ltvHaircut: 0.70, feeRate: 0.16, subLandAdvanceFactor: 1.00 },
  { level: 6,  minCombinedScore: 20,  minRepayments: 0,  ltvHaircut: 0.68, feeRate: 0.18, subLandAdvanceFactor: 0.95 },
  { level: 7,  minCombinedScore: 10,  minRepayments: 2,  ltvHaircut: 0.67, feeRate: 0.18, subLandAdvanceFactor: 1.00 },
  { level: 8,  minCombinedScore: 8,   minRepayments: 0,  ltvHaircut: 0.65, feeRate: 0.20, subLandAdvanceFactor: 0.90 },
  { level: 9,  minCombinedScore: 5,   minRepayments: 0,  ltvHaircut: 0.60, feeRate: 0.25, subLandAdvanceFactor: 0.85 },
  { level: 10, minCombinedScore: 2,   minRepayments: 0,  ltvHaircut: 0.55, feeRate: 0.30, subLandAdvanceFactor: 0.75 },
] as const;

/** Hard ceiling applied by MIN(90%, …) in I3 and I4. */
export const MAX_LTV_CAP = 0.90;

/** Loan caps (C6): standard = $5M, LOC = $5M. */
export const LOAN_CAPS = {
  default: 5_000_000,      // standard loanType
  lineOfCredit: 5_000_000, // LOC loanType
  smallLoanThreshold: 200_000, // loans below this are flagged "SMALL LOAN"
} as const;

/** Pre-sale deposit threshold separating AK49 tiers (2.5%). */
export const PRESALE_DEPOSIT_THRESHOLD = 0.025;

/** Credit score boundaries used throughout the scoring matrix (row 54). */
export const CREDIT_SCORE_THRESHOLDS = {
  veryLow: 550,   // T54 — second LTC haircut bracket
  low: 600,       // V54 — first LTC haircut bracket
  minimum: 620,   // X54 — −3% LTV bracket
  fair: 699,      // Y54 — −1.5% LTV bracket ceiling
  good: 740,      // AA54 — +1.5% LTV bonus floor
  excellent: 780, // AB54 — +3% LTV bonus floor
} as const;

/** Net worth thresholds (row 52). */
export const NET_WORTH_THRESHOLDS = {
  ltcHaircut: 100_000,   // V52 — 5% LTC haircut if below
  ltvPenalty: 500_000,   // X52 — −3% LTV if below
  // AG52: +1.5% bonus for NW strictly > $3.5M.
  // AI52's +3% tier requires combined guarantor-table NW ≥ $5M (AE52); in the
  // app's simplified single-input mode AE52=0, so AI52 never fires.
  bonusFloor: 3_500_000, // AA52 — +1.5% LTV bonus (strictly >)
} as const;

/** Bank balance thresholds (row 53). */
export const BANK_BALANCE_THRESHOLDS = {
  ltcHaircut: 5_000,     // V53 — 5% LTC haircut if below
  heavyPenalty: 50_000,  // X53 — −3% LTV if strictly below
  lightPenalty: 75_000,  // Y53 — −1.5% LTV if strictly between $50K and $75K
  bonusSmall: 250_000,   // AA53 — +1.5% LTV bonus floor
  bonusLarge: 500_000,   // AB53 — +3% LTV bonus floor
  c9Cap: 25_000,         // threshold for M58 LTV cap via C9
  c9LowCap: 10_000,      // M58: <$10K → 55% cap; <$25K → 60% cap
} as const;

/**
 * LTV size-limit tiers (I8 formula, C8 scenario).
 * Based on appraised value per unit, not unit count.
 *   value/unit > $2M → 65%
 *   value/unit > $1M → 70%
 *   value/unit ≤ $1M → 75%
 */
export const LTV_SIZE_LIMIT_TIERS = {
  highValuePerUnit: 2_000_000,  // above → 65%
  midValuePerUnit: 1_000_000,   // above → 70%
  highRate: 0.65,
  midRate: 0.70,
  baseRate: 0.75,
} as const;

/** Appraisal red-flag LTV cap applied via C9 (L45 → M48 → N48). */
export const APPRAISAL_RED_FLAG_LTV_CAP = 0.70;

/** Population Low: LTC haircut (AK45). */
export const POPULATION_LOW_LTC_HAIRCUT = 0.10;

export const UNAVAILABLE_STATES = ['ND', 'HI', 'AK'];

/**
 * Line-of-credit base LTV table (Excel rows 115–118, columns B/C/E).
 *
 *   1 unit:   appraised > $2.5M → 70%, else 74%.
 *   2–4 unit: value-per-unit > $250K → 72%, else 74%.
 *   5+ unit:  always 68% (overrides per-unit tier per F118).
 *
 * See I118 formula in docs/Estimator-ORIG.xlsx for the full lookup.
 */
export const LOC_LTV = {
    base: 0.74,
    singleUnitHighValueCap: 2_500_000,    // C116
    singleUnitHighValueRate: 0.70,        // B117
    multiUnitPerUnitCap: 250_000,         // E115
    multiUnitHighRate: 0.72,              // B116
    fivePlusUnitRate: 0.68,               // B118
    fivePlusUnitThreshold: 5,             // I115 = (units ≥ 5)
} as const;

export const AFFORDABILITY_OPTIONS = [
    { id: 'Excellent',   label: 'Excellent — most can readily afford' },
    { id: 'Affordable',  label: 'Affordable — average income qualifies' },
    { id: 'Weak',        label: 'Weak — stretch for many' },
    { id: 'Poor',        label: 'Poor — unaffordable at avg income' },
];

export const POP_DENSITY_OPTIONS = [
    { id: 'Dense',          label: 'Dense — 50,000+' },
    { id: 'Moderate-Dense', label: 'Moderate-Dense — 25,000–50,000' },
    { id: 'Moderate-Low',   label: 'Moderate-Low — 10,000–25,000' },
    { id: 'Low',            label: 'Low — under 10,000' },
];

export const POP_GROWTH_OPTIONS = [
    { id: 'Fast',     label: 'Fast — strong projected growth' },
    { id: 'Moderate', label: 'Moderate — steady growth' },
    { id: 'Flat',     label: 'Flat — little to no growth' },
    { id: 'Negative', label: 'Negative — projected decline' },
];
